import type { FieldError, MonitoredField } from 'domain/monitored';
import type { ConnectivityDetails, GeoPosition, Vehicle } from 'domain/vehicle';

import type { ConnectivityStatusFieldV2, ErrorV2, PositionFieldV2, VehicleV2 } from 'API';

const toDate = (raw?: string | null): Date | null => {
    if (raw == null || raw === '') {
        return null;
    }
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? null : date;
};

const toFieldError = (error?: ErrorV2 | null): FieldError | null => {
    if (error == null) {
        return null;
    }
    return { type: error.type, updatedAt: toDate(error.raisedAt) };
};

const connectivityFromV2 = (field?: ConnectivityStatusFieldV2 | null): MonitoredField<ConnectivityDetails> => {
    if (field == null) {
        return {};
    }
    const ttl = field.ttl ?? null;
    const error = toFieldError(field.error);
    if (field.value == null) {
        return { ttl, value: null, error };
    }
    return {
        ttl,
        value: {
            value: {
                state: field.value.state ?? 'UNKNOWN',
                expectDisconnection: field.value.expectDisconnection,
                latency: field.value.latency ?? undefined,
            },
            updatedAt: toDate(field.value.updatedAt),
        },
        error,
    };
};

const positionFromV2 = (field?: PositionFieldV2 | null): MonitoredField<GeoPosition> => {
    if (field == null) {
        return {};
    }
    const ttl = field.ttl ?? null;
    const error = toFieldError(field.error);
    const raw = field.value?.position;
    if (field.value == null || raw == null || typeof raw.latitude !== 'number' || typeof raw.longitude !== 'number') {
        return { ttl, value: null, error };
    }
    const position: GeoPosition = { lat: raw.latitude, lng: raw.longitude };
    if (typeof raw.heading === 'number') {
        position.heading = raw.heading;
    }
    if (typeof raw.altitude === 'number') {
        position.altitudeMeters = raw.altitude;
    }
    return {
        ttl,
        value: { value: position, updatedAt: toDate(field.value.updatedAt) },
        error,
    };
};

export interface VehicleLiveFields {
    connectivity: MonitoredField<ConnectivityDetails>;
    position: MonitoredField<GeoPosition>;
    latestVehicleUpdate: Date | null;
    timestamp: Date | null;
}

export const vehicleLiveFieldsFromV2 = (live: VehicleV2): VehicleLiveFields => ({
    connectivity: connectivityFromV2(live.connectivityStatus),
    position: positionFromV2(live.position),
    latestVehicleUpdate: toDate(live.latestVehicleUpdate),
    timestamp: toDate(live.timestamp),
});

export const applyVehicleLiveUpdate = (target: Vehicle, live: VehicleV2): void => {
    const fields = vehicleLiveFieldsFromV2(live);
    target.connectivity = fields.connectivity;
    target.position = fields.position;
    target.latestVehicleUpdate = fields.latestVehicleUpdate;
    target.timestamp = fields.timestamp;
};
